import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { catchError } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { StorageConst } from '../consts/storage-consts';
import { RoutesUrl } from '../consts/routes';
import { AuthService } from '../../auth/auth.service';
import packageInfo from '../../../../package.json';

@Injectable({
  providedIn: 'root'
})
export class HttpInterceptorService implements HttpInterceptor {
  constructor(private auth: AuthService) {
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const isAppApiRequest = this.isAppApiRequest(req.url);

    if (req.headers.has('X-Skip-Interceptor')) {
      // Request already has a token, proceed without modification
      req = req.clone({
        url: this.multiVersionURL(req.url),
        withCredentials: isAppApiRequest,
        setHeaders: {
          Authorization: req.headers.get("Authorization"),
          'X-Client-Version': packageInfo.version,
        }
      });
      return next.handle(req);
    }
    // Get the auth token from the service.

    // Clone the request and replace the original headers with
    // cloned headers, updated with the authorization.
    req = req.clone({
      url: this.multiVersionURL(req.url),
      withCredentials: isAppApiRequest,
    });

    // Local systemAPI is often http://localhost:4202 (no "api" in the URL). Match configured
    // API bases so Bearer is still attached for FormService/BaseAPIService calls.
    if (isAppApiRequest) {
      req = req.clone({
        setHeaders: {
          Authorization: `Bearer ${sessionStorage.getItem(StorageConst.TOKEN)}`,
          'X-Client-Version': packageInfo.version,
        }
      });
    }

    // send cloned request with header to the next handler.
    return next.handle(req).pipe(
      catchError((err: HttpErrorResponse) => {
        const isErrorFromBiggerServer: boolean = err.url.includes(environment.systemAPI) || err.url.includes(environment.venditioAPI);

        if (isErrorFromBiggerServer && err.status === 401) {

          const isOnLoginPage: boolean = location.href.includes('session/signin');

          if (!isOnLoginPage) {
            sessionStorage.removeItem(StorageConst.TOKEN);
            sessionStorage.removeItem(StorageConst.USER);
            // This is a HARD reload, which wipes the in-memory AuthService.redirectUrl. Persist the
            // originally-requested deep link so the login screen can return to it (e.g. /invcounter).
            const currentUrl: string = window.location.pathname + window.location.search;
            if (currentUrl && currentUrl !== '/' && !currentUrl.includes('/session/')) {
              sessionStorage.setItem(StorageConst.RETURN_URL, currentUrl);
            }
            const accessCode: string = sessionStorage.getItem(StorageConst.ACCESS_CODE);
            window.location.href = accessCode ?
              `${RoutesUrl.session.signIn}?accessCode=${accessCode}`
              :
              RoutesUrl.session.signIn;
          }
        }
        return throwError(err);
      })
    );
  }

  private multiVersionURL(url: string): string {
    const {siteAPIEnvironment, adminAPIEnvironment} = this.auth.betaFlags;
    const regex = new RegExp(/\/\/([^.]+)/);
    if (url.indexOf('/siteAPI/') > -1 && siteAPIEnvironment > 1) {
      return url.replace(regex, `//$1-${siteAPIEnvironment}`);
    } else if (url.indexOf('/adminAPI/') > -1 && adminAPIEnvironment > 1) {
      return url.replace(regex, `//$1-${adminAPIEnvironment}`);
    } else {
      return url;
    }
  }

  /** True for admin/site API calls, including local systemAPI without "/adminAPI" or "api" in the path. */
  private isAppApiRequest(url: string): boolean {
    if (!url) {
      return false;
    }
    if (url.indexOf('api') > -1) {
      return true;
    }
    const systemAPI = environment.systemAPI;
    const venditioAPI = environment.venditioAPI;
    return (!!systemAPI && url.indexOf(systemAPI) > -1)
      || (!!venditioAPI && url.indexOf(venditioAPI) > -1);
  }
}
